// StatusPill — the status-changer control that lives next to the Back button
// in the Command Center nav.
//
// Clicking opens a small menu of the five Phase 5a statuses; selecting one
// fires useUpdateStatus. Selecting "Snoozed" asks the parent to open the
// SnoozeMenu (the parent keeps snooze popover state hoisted so the keyboard
// shortcut Z and the click path share a single source of truth).
//
// While a mutation is pending, the pill optimistically renders the target
// status so clicks feel instant.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationStatus } from '@md/shared';
import { CONVERSATION_STATUSES } from '@md/shared';
import { useUpdateStatus } from '@/lib/hooks/useConversation';
type StatusPillProps = {
  conversationId: string;
  status: ConversationStatus;
  snoozedUntil: string | null;
  /** Parent controls the SnoozeMenu popover (shared with the Z shortcut). */
  snoozeOpen: boolean;
  onOpenSnooze: () => void;
  onCloseSnooze: () => void;
};
const STATUS_LABELS: Record<ConversationStatus, string> = {
  open: 'Open',
  awaiting: 'Awaiting',
  snoozed: 'Snoozed',
  resolved: 'Resolved',
  spam: 'Spam',
};
// Color dots next to each menu item + the pill's border tint.
// HSL values tuned for the MD stone/eggshell palette.
const STATUS_HUE: Record<ConversationStatus, string> = {
  open: '28 80% 55%', // orange
  awaiting: '48 85% 55%', // yellow
  snoozed: '210 70% 55%', // blue
  resolved: '140 45% 45%', // green
  spam: '0 0% 55%', // neutral gray
};
/**
 * Short relative label for a snoozed_until timestamp, e.g. "2h" or "3d".
 * Returns 'waking' once the timestamp is in the past (handled by wake-snoozed).
 */
function formatSnoozeShort(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'waking';
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return 'soon';
  if (hours < 48) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}
export function StatusPill({
  conversationId,
  status,
  snoozedUntil,
  snoozeOpen,
  onOpenSnooze,
  onCloseSnooze,
}: StatusPillProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const update = useUpdateStatus();
  const navigate = useNavigate();
  // Optimistic display: while the mutation is in flight, show the pending status.
  const displayStatus: ConversationStatus =
    update.isPending && update.variables ? update.variables.status : status;
  const displaySnoozed = displayStatus === 'snoozed' ? snoozedUntil : null;
  // Close the status menu on outside click. (Snooze popover close is parent's job.)
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);
  const handlePick = (next: ConversationStatus) => {
    setMenuOpen(false);
    if (next === 'snoozed') {
      onOpenSnooze();
      return;
    }
    update.mutate(
      { id: conversationId, status: next },
      { onSuccess: () => navigate('/') },
    );
  };
  const shortSnooze = formatSnoozeShort(displaySnoozed);
  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => {
          if (snoozeOpen) onCloseSnooze();
          setMenuOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="Change status"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          border: `1px solid hsl(${STATUS_HUE[displayStatus]} / 0.45)`,
          background: `hsl(${STATUS_HUE[displayStatus]} / 0.12)`,
          color: `hsl(${STATUS_HUE[displayStatus]} / 1)`,
          fontSize: 13,
          lineHeight: 1.2,
          cursor: 'pointer',
          opacity: update.isPending ? 0.7 : 1,
          transition: 'background 150ms, border-color 150ms, opacity 150ms',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: `hsl(${STATUS_HUE[displayStatus]})`,
          }}
        />
        <span style={{ fontWeight: 500 }}>{STATUS_LABELS[displayStatus]}</span>
        {shortSnooze ? (
          <span style={{ color: 'hsl(var(--ink) / 0.55)', fontSize: 12 }}>· {shortSnooze}</span>
        ) : null}
      </button>
      {menuOpen ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: 180,
            background: 'white',
            border: '1px solid hsl(var(--ink) / 0.12)',
            borderRadius: 10,
            boxShadow: '0 8px 24px hsl(var(--ink) / 0.08)',
            padding: 6,
            zIndex: 40,
          }}
        >
          {CONVERSATION_STATUSES.map((s) => (
            <button
              key={s}
              role="menuitem"
              type="button"
              onClick={() => handlePick(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '6px 10px',
                borderRadius: 6,
                border: 'none',
                background: s === status ? 'hsl(var(--ink) / 0.04)' : 'transparent',
                color: 'hsl(var(--ink) / 0.9)',
                cursor: 'pointer',
                fontSize: 13,
                textAlign: 'left',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'hsl(var(--ink) / 0.06)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  s === status ? 'hsl(var(--ink) / 0.04)' : 'transparent')
              }
            >
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: `hsl(${STATUS_HUE[s]})`,
                  flex: 'none',
                }}
              />
              <span style={{ flex: 1 }}>{STATUS_LABELS[s]}</span>
              {s === status ? (
                <span aria-hidden style={{ color: 'hsl(var(--ink) / 0.45)' }}>
                  ✓
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
